import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useBrand } from '../../../app/config/brand';
import { useApi } from '../../../api/useApi';
import { Icons } from '../../../shared/icons/Icons';

interface OrderFee {
  description: string;
  amount: number;
}

interface OrderData {
  id: string;
  orderNumber: string;
  type: string;
  status: string;
  caseTitle?: string;
  caseNumber?: string;
  courtLocation?: string;
  totalAmount?: number;
  createdAt: string;
  submittedAt?: string;
  documents?: Array<{ originalFileName: string }>;
  efmFiling?: {
    metadata?: {
      fees?: { totalAmount: number; lineItems: OrderFee[] };
    };
  };
  typeData?: {
    orderInfo?: { county?: string };
    caseInfo?: { caseCategory?: string; caseType?: string };
  };
}

const OrderConfirmation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const brand = useBrand();
  const { get, isAuthenticated } = useApi();
  const [order, setOrder] = useState<OrderData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !id) return;
    let cancelled = false;

    (async () => {
      try {
        const data = await get<OrderData>(`/api/orders/${id}`);
        if (!cancelled) setOrder(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load order');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [get, isAuthenticated, id]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: brand.colors.bgLight }}>
        <div className="animate-pulse text-center">
          <div className="w-16 h-16 rounded-full bg-gray-200 mx-auto mb-4" />
          <div className="h-6 w-48 bg-gray-200 rounded mx-auto mb-2" />
          <div className="h-4 w-64 bg-gray-200 rounded mx-auto" />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: brand.colors.bgLight }}>
        <div className="text-center">
          <Icons.X className="w-12 h-12 mx-auto mb-3" style={{ color: brand.colors.error }} />
          <h2 className="text-lg font-semibold mb-1" style={{ color: brand.colors.secondary }}>
            Order not found
          </h2>
          <p className="text-sm mb-4" style={{ color: brand.colors.textLight }}>
            {error || 'Unable to load this order.'}
          </p>
          <button
            onClick={() => navigate('/orders')}
            className="px-4 py-2 text-sm font-medium rounded-lg"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const submittedDate = order.submittedAt
    ? new Date(order.submittedAt)
    : new Date(order.createdAt);
  const formattedDate = submittedDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const formattedTime = submittedDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });

  const fees = order.efmFiling?.metadata?.fees;
  const courtName = order.courtLocation || order.typeData?.orderInfo?.county || 'the court';
  const filingType = [
    order.typeData?.caseInfo?.caseCategory,
    order.typeData?.caseInfo?.caseType,
  ].filter(Boolean).join(' · ') || 'Filing';
  const docName = order.documents?.[0]?.originalFileName || '—';

  const estReviewDate = new Date(submittedDate);
  estReviewDate.setDate(estReviewDate.getDate() + 2);
  const estReviewFormatted = estReviewDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="flex-1 overflow-auto" style={{ backgroundColor: brand.colors.bgLight }}>
      <div className="max-w-3xl mx-auto px-6 py-12">

        {/* Success Hero */}
        <section
          className="text-center rounded-2xl p-10 mb-6"
          style={{
            background: `linear-gradient(180deg, ${brand.colors.bgAccent} 0%, white 100%)`,
            border: `1px solid ${brand.colors.border}`,
          }}
        >
          <div
            className="w-[72px] h-[72px] rounded-full mx-auto mb-4 flex items-center justify-center text-white text-4xl font-extrabold"
            style={{
              backgroundColor: brand.colors.success,
              boxShadow: `0 0 0 8px rgba(15, 157, 107, 0.12)`,
            }}
          >
            ✓
          </div>
          <h1
            className="text-2xl font-bold tracking-tight mb-1"
            style={{ color: brand.colors.secondary, fontFamily: brand.typography.fontFamily.heading }}
          >
            Filing submitted to the court
          </h1>
          <p className="text-sm mb-5" style={{ color: brand.colors.textMuted }}>
            We've sent your documents to {courtName}. You'll get an email
            when the clerk accepts the filing — usually within 24–48 hours.
          </p>
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}`, color: brand.colors.text }}
          >
            <span style={{ color: brand.colors.textMuted, fontWeight: 500 }}>Order #</span>
            <span>{order.orderNumber}</span>
          </div>
          <div className="flex justify-center gap-3 mt-6">
            <button
              onClick={() => navigate(`/orders/${order.id}`)}
              className="px-4 py-2 text-sm font-medium rounded-lg border"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              View receipt
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-sm font-medium rounded-lg"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              Back to dashboard
            </button>
          </div>
        </section>

        {/* Receipt */}
        <section
          className="rounded-xl p-6 mb-6 shadow-xs"
          style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
        >
          <div
            className="flex items-start justify-between pb-4 mb-4"
            style={{ borderBottom: `1px solid ${brand.colors.border}` }}
          >
            <div>
              <h2 className="text-base font-bold mb-0.5" style={{ color: brand.colors.secondary }}>Receipt</h2>
              <div className="text-xs" style={{ color: brand.colors.textMuted }}>
                {formattedDate} · {formattedTime}
              </div>
            </div>
            <button
              className="inline-flex items-center gap-1.5 text-sm font-semibold"
              style={{ color: brand.colors.link }}
            >
              <Icons.Download className="w-4 h-4" />
              Download PDF
            </button>
          </div>

          {/* Case Info */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm mb-5">
            <span style={{ color: brand.colors.textMuted }}>Case</span>
            <span className="text-right font-medium" style={{ color: brand.colors.text }}>
              {order.caseTitle || '—'}
            </span>
            <span style={{ color: brand.colors.textMuted }}>Court</span>
            <span className="text-right font-medium" style={{ color: brand.colors.text }}>
              {courtName}
            </span>
            <span style={{ color: brand.colors.textMuted }}>Filing type</span>
            <span className="text-right font-medium" style={{ color: brand.colors.text }}>
              {filingType}
            </span>
            <span style={{ color: brand.colors.textMuted }}>Documents</span>
            <span className="text-right font-medium" style={{ color: brand.colors.text }}>
              {docName}
            </span>
          </div>

          {/* Fee Lines */}
          <div className="mb-4">
            {fees?.lineItems?.map(fee => (
              <div
                key={fee.description}
                className="flex justify-between py-1.5 text-sm"
                style={{ borderBottom: `1px solid ${brand.colors.border}` }}
              >
                <span style={{ color: brand.colors.text }}>{fee.description}</span>
                <span style={{ color: brand.colors.text }}>${fee.amount.toFixed(2)}</span>
              </div>
            )) ?? (
              <div
                className="flex justify-between py-1.5 text-sm"
                style={{ borderBottom: `1px solid ${brand.colors.border}` }}
              >
                <span style={{ color: brand.colors.text }}>Total</span>
                <span style={{ color: brand.colors.text }}>
                  ${order.totalAmount?.toFixed(2) ?? '0.00'}
                </span>
              </div>
            )}
            <div
              className="flex justify-between pt-3 mt-1 font-bold text-base"
              style={{ borderTop: `2px solid ${brand.colors.border}`, color: brand.colors.link }}
            >
              <span>Total charged</span>
              <span>${(fees?.totalAmount ?? order.totalAmount ?? 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-between pt-3 text-xs"
            style={{ borderTop: `1px dashed ${brand.colors.border}`, color: brand.colors.textMuted }}
          >
            <span>Reference: {order.orderNumber}</span>
            <span>
              Need a corrected receipt?{' '}
              <a href={`mailto:${brand.contact.email}`} className="font-semibold" style={{ color: brand.colors.link }}>
                Contact support
              </a>
            </span>
          </div>
        </section>

        {/* Timeline */}
        <section
          className="rounded-xl p-6 mb-6 shadow-xs"
          style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
        >
          <h3 className="text-base font-bold mb-4" style={{ color: brand.colors.secondary }}>
            What happens next
          </h3>
          <ul className="space-y-0">
            <TimelineStep
              step="done"
              label={`Submitted to ${brand.company.name}`}
              detail="We received your filing and payment."
              time="Just now"
              brand={brand}
            />
            <TimelineStep
              step="current"
              label={`Sent to ${courtName}`}
              detail="In transit to the court — usually within minutes during business hours."
              time="In progress"
              brand={brand}
            />
            <TimelineStep
              step="pending"
              number={3}
              label="Court review"
              detail="The clerk reviews your filing. Most are accepted in 24–48 business hours."
              time={`Est. by ${estReviewFormatted}`}
              brand={brand}
            />
            <TimelineStep
              step="pending"
              number={4}
              label="Confirmation email"
              detail="We'll email you the court-stamped copy and update this order."
              brand={brand}
            />
          </ul>
        </section>

        {/* Help Card */}
        <section
          className="rounded-xl p-5 flex items-center gap-4"
          style={{ backgroundColor: brand.colors.bgAccent, border: `1px solid ${brand.colors.border}` }}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-xl shrink-0"
            style={{ backgroundColor: 'white' }}
          >
            💬
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold" style={{ color: brand.colors.secondary }}>
              Questions about your filing?
            </div>
            <div className="text-sm mt-0.5" style={{ color: brand.colors.textMuted }}>
              Our team is available 8 AM – 6 PM PT. Average response: 2 minutes.
            </div>
          </div>
          <button
            className="px-4 py-2 text-sm font-medium rounded-lg border shrink-0"
            style={{ borderColor: brand.colors.border, color: brand.colors.text, backgroundColor: 'white' }}
          >
            Chat with us
          </button>
        </section>
      </div>
    </div>
  );
};

interface TimelineStepProps {
  step: 'done' | 'current' | 'pending';
  number?: number;
  label: string;
  detail: string;
  time?: string;
  brand: ReturnType<typeof useBrand>;
}

const TimelineStep: React.FC<TimelineStepProps> = ({ step, number, label, detail, time, brand }) => {
  const dotStyle: React.CSSProperties = step === 'done'
    ? { backgroundColor: brand.colors.success, borderColor: brand.colors.success, color: 'white' }
    : step === 'current'
      ? { backgroundColor: brand.colors.primary, borderColor: brand.colors.primary, color: brand.colors.onPrimary }
      : { backgroundColor: 'white', borderColor: brand.colors.border, color: brand.colors.textMuted };

  return (
    <li className="flex gap-3.5 py-3 items-start">
      <span
        className="w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold shrink-0"
        style={dotStyle}
      >
        {step === 'done' ? '✓' : number ?? '•'}
      </span>
      <div className="flex-1">
        <div className="text-sm font-semibold" style={{ color: brand.colors.text }}>{label}</div>
        <div className="text-xs mt-0.5" style={{ color: brand.colors.textMuted }}>{detail}</div>
      </div>
      {time && (
        <span className="text-xs shrink-0 pt-0.5" style={{ color: brand.colors.textMuted }}>
          {time}
        </span>
      )}
    </li>
  );
};

export default OrderConfirmation;
